import * as React from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Zap, ArrowRight, Volume2, Trophy, ArrowLeft, RotateCcw, Loader2, CheckCircle2, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import {
  fetchTopics, fetchTopicInfo, fetchTopicVocab, fetchTopicMastery,
  saveProgress, submitQuizSession,
  resolveStartTopic, nextTopicAfter, setRecentTopicId,
  type Topic, type VocabWord, type TopicMastery
} from "@/lib/queries";
import { playWordClick, speakWord } from "@/lib/audio";
import { playCorrect, playWrong, playReveal, playSessionEnd } from "@/lib/sounds";
import { flyStarsToPill, confettiForResult } from "@/lib/effects";
import { cn } from "@/lib/utils";

const QUIZ_LENGTH = 10;
const QUICK_LENGTH = 5;
const MIN_VOCAB   = 6;
const NEXT_DELAY_MS = 900;

type Phase = "loading" | "no-topics" | "no-vocab" | "playing" | "results";
type Direction = "ar-en" | "en-ar";
type Option = { id: string; label: string; lang: "en" | "ar"; isCorrect: boolean };
type Question = { word: VocabWord; direction: Direction; options: Option[] };

export default function Quiz() {
  const [params] = useSearchParams();
  const urlTopic = params.get("topic");
  const mode    = params.get("mode") === "quick" ? "quick" : "full";
  const roundLen = mode === "quick" ? QUICK_LENGTH : QUIZ_LENGTH;
  const nav = useNavigate();

  const [phase, setPhase] = React.useState<Phase>("loading");
  const [topic, setTopic] = React.useState<Topic | null>(null);
  const [vocab, setVocab] = React.useState<VocabWord[]>([]);
  const [topics, setTopics] = React.useState<Topic[]>([]);
  const [mastery, setMastery] = React.useState<Map<string, TopicMastery>>(new Map());

  const [idx, setIdx]         = React.useState(0);
  const [question, setQ]      = React.useState<Question | null>(null);
  const [picked,   setPicked] = React.useState<string | null>(null);
  const [correctCt, setCorrect] = React.useState(0);
  const [wrongHistory, setWrongHistory] = React.useState<VocabWord[]>([]);

  const startTimeRef = React.useRef<Date>(new Date());
  const [submitting, setSubmitting]   = React.useState(false);
  const [awardedRow, setAwardedRow]   = React.useState<{ new_total: number; points_awarded: number } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      const [allTopics, mast] = await Promise.all([fetchTopics(), fetchTopicMastery()]);
      if (cancelled) return;
      setTopics(allTopics);
      setMastery(mast);

      // Resolve the topic to start: URL param > recent > first-unfinished > first.
      let resolved: Topic | null = null;
      if (urlTopic) {
        resolved = await fetchTopicInfo(urlTopic);
      }
      if (!resolved) resolved = await resolveStartTopic(allTopics, mast);

      if (!resolved) { setPhase("no-topics"); return; }

      setRecentTopicId(resolved.id);
      const words = await fetchTopicVocab(resolved.id);
      if (cancelled) return;

      setTopic(resolved);
      setVocab(words);
      if (words.length < MIN_VOCAB) { setPhase("no-vocab"); return; }

      startTimeRef.current = new Date();
      setCorrect(0);
      setWrongHistory([]);
      setIdx(0);
      const q = buildQuestion(words, []);
      setQ(q);
      setPicked(null);
      setPhase("playing");
      playReveal();
      if (q.direction === "ar-en") void speakWord(q.word);
    })();
    return () => { cancelled = true; };
  }, [urlTopic, mode]);

  function next() {
    setPicked(null);
    const nextIdx = idx + 1;
    if (nextIdx >= roundLen) { void endRound(); return; }
    setIdx(nextIdx);
    const q = buildQuestion(vocab, []);
    setQ(q);
    playReveal();
    if (q.direction === "ar-en") void speakWord(q.word);
  }

  async function endRound() {
    setPhase("results");
    setSubmitting(true);
    // Audio + visual celebration.
    playSessionEnd(correctCt, roundLen);
    window.setTimeout(() => {
      const card = document.querySelector("[data-results-card]");
      confettiForResult({ correct: correctCt, total: roundLen, originEl: card });
    }, 80);
    try {
      const result = await submitQuizSession({
        claimed_correct: correctCt,
        session_started: startTimeRef.current,
        mode:            mode === "quick" ? "quiz_quick" : "quiz"
      });
      if (result) setAwardedRow(result);
    } catch (e) { console.warn("[quiz] submit", e); }
    setSubmitting(false);
  }

  function pickAnswer(opt: Option, btn?: HTMLElement | null) {
    if (picked) return;
    setPicked(opt.id);
    const isCorrect = opt.isCorrect;
    if (question?.word) {
      void saveProgress({
        word_id: question.word.id,
        correct: isCorrect,
        mode:    mode === "quick" ? "quiz_quick" : "quiz",
        points:  isCorrect ? 10 : 0
      });
    }
    if (isCorrect) {
      setCorrect(c => c + 1);
      playCorrect();
      flyStarsToPill(btn ?? null);
    } else {
      playWrong();
      if (question?.word) setWrongHistory(prev => [...prev, question.word]);
    }
    setTimeout(next, NEXT_DELAY_MS);
  }

  function goNextTopic() {
    if (!topic) return;
    const next = nextTopicAfter(topic.id, topics);
    if (next) {
      setRecentTopicId(next.id);
      nav(`/quiz?topic=${encodeURIComponent(next.id)}${mode === "quick" ? "&mode=quick" : ""}`);
    }
  }

  const nextTopic = topic ? nextTopicAfter(topic.id, topics) : null;
  const topicProgress = topic ? mastery.get(topic.id) : null;

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[900px] pointer-events-none" density={40} />

        <div className="relative mx-auto max-w-3xl px-4 md:px-6 pt-10 pb-32">

          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </Button>
            {phase === "playing" && <Badge variant="accent">Q {idx + 1} / {roundLen}</Badge>}
          </div>

          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Quiz</h1>
            </div>
            {topic && (
              <div className="flex flex-wrap items-baseline gap-3">
                <p className="text-fg-2">
                  <span className="text-accent">{topic.name_en}</span>
                  <span className="text-fg-3"> · {vocab.length} words</span>
                </p>
                {topicProgress && (
                  <span className="text-xs text-fg-3">
                    Mastered <span className="text-fg-2 font-semibold">{topicProgress.correct}/{topicProgress.total}</span> ({topicProgress.pct}%)
                  </span>
                )}
              </div>
            )}
          </BlurReveal>

          {phase === "playing" && (
            <div className="mt-4 h-1.5 rounded-full bg-bg-2 overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${(idx / roundLen) * 100}%` }} />
            </div>
          )}

          <div className="mt-8">
            {phase === "loading"   && <Spinner />}
            {phase === "no-topics" && <NoTopicsCard />}
            {phase === "no-vocab"  && <NoVocabCard count={vocab.length} />}
            {phase === "playing" && question && (
              <QuestionCard question={question} picked={picked} onPick={pickAnswer} />
            )}
            {phase === "results" && (
              <ResultsCard
                correct={correctCt}
                total={roundLen}
                submitting={submitting}
                awarded={awardedRow}
                wrongHistory={wrongHistory}
                onRetry={() => nav(0)}
                onHome={() => nav("/dashboard")}
                onNextTopic={nextTopic ? goNextTopic : null}
                nextTopicName={nextTopic?.name_en}
              />
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

/* ─────────────── pieces ─────────────── */

function QuestionCard({ question, picked, onPick }: {
  question: Question;
  picked: string | null;
  onPick: (opt: Option, btn: HTMLElement | null) => void;
}) {
  const w = question.word;
  return (
    <BlurReveal>
      <Card className="text-center relative overflow-hidden">
        <CardContent className="py-10">
          <div className="text-xs uppercase tracking-widest text-fg-3">
            {question.direction === "ar-en" ? "What does this mean?" : "What's the Arabic for…"}
          </div>
          <div className={cn(
            "mt-5 font-bold text-accent",
            question.direction === "ar-en" ? "text-6xl md:text-7xl" : "text-4xl md:text-5xl"
          )} style={question.direction === "ar-en"
            ? { fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }
            : undefined}>
            {question.direction === "ar-en" ? w.arabic : w.english}
          </div>
          {question.direction === "ar-en" && w.transliteration && (
            <div className="mt-2 text-lg text-fg-2">{w.transliteration}</div>
          )}
          {question.direction === "ar-en" && (
            <Button variant="ghost" size="sm" className="mt-3" onClick={() => playWordClick(w)}>
              <Volume2 className="w-4 h-4" /> Listen
            </Button>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {question.options.map((opt, i) => {
              const isPicked  = picked === opt.id;
              const showRight = picked !== null && opt.isCorrect;
              const showWrong = isPicked && !opt.isCorrect;
              return (
                <button key={opt.id} type="button"
                  disabled={picked !== null}
                  onClick={e => onPick(opt, e.currentTarget)}
                  className={cn(
                    "rounded-2xl border px-5 py-4 text-left transition-colors flex items-start gap-3",
                    !picked && "border-line bg-bg hover:bg-bg-3 hover:border-line-2",
                    showRight && "border-ok/40 bg-ok/10 text-ok",
                    showWrong && "border-bad/40 bg-bad/10 text-bad",
                    picked && !showRight && !showWrong && "border-line bg-bg-3/30 text-fg-2 opacity-60"
                  )}
                  style={opt.lang === "ar" ? { fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" } : undefined}>
                  <span className="text-xs uppercase tracking-widest text-fg-3 shrink-0">{String.fromCharCode(65 + i)}</span>
                  <span className={cn("flex-1", opt.lang === "ar" && "text-lg")}>{opt.label}</span>
                  {showRight && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                  {showWrong && <X className="w-4 h-4 shrink-0" />}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </BlurReveal>
  );
}

function ResultsCard({
  correct, total, submitting, awarded, wrongHistory, onRetry, onHome,
  onNextTopic, nextTopicName
}: {
  correct: number; total: number;
  submitting: boolean;
  awarded: { new_total: number; points_awarded: number } | null;
  wrongHistory: VocabWord[];
  onRetry: () => void;
  onHome:  () => void;
  onNextTopic: (() => void) | null;
  nextTopicName: string | undefined;
}) {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : pct > 0 ? 1 : 0;
  const mastered = pct >= 80;
  return (
    <BlurReveal>
      <Card data-results-card>
        <CardContent className="text-center py-10">
          <div className="flex justify-center gap-1 mb-4 text-2xl">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} className={i < stars ? "text-accent" : "text-fg-3 opacity-30"}>★</span>
            ))}
          </div>
          <div className="text-xs uppercase tracking-widest text-fg-3">Round complete</div>
          <h2 className="text-4xl font-bold tracking-tight mt-2">
            {correct} / {total} <span className="text-fg-3 font-normal">correct</span>
          </h2>

          {submitting ? (
            <p className="mt-4 text-fg-2 inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-accent" /> Banking points…
            </p>
          ) : awarded ? (
            <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-accent-lo bg-accent/10 px-5 py-3">
              <Trophy className="w-5 h-5 text-accent" />
              <span className="text-accent font-bold text-xl">+{awarded.points_awarded.toLocaleString()}</span>
              <span className="text-xs uppercase tracking-widest text-fg-3">points</span>
              <span className="text-xs text-fg-2">· new total {awarded.new_total.toLocaleString()}</span>
            </div>
          ) : (
            <p className="mt-4 text-fg-2 text-sm">Points couldn't sync — try again in a moment.</p>
          )}

          {wrongHistory.length > 0 && (
            <div className="mt-7 text-left">
              <h3 className="text-xs uppercase tracking-widest text-fg-3 mb-2">Words to revisit</h3>
              <ul className="rounded-2xl border border-line bg-bg p-2 divide-y divide-line">
                {wrongHistory.map(w => (
                  <li key={w.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="text-lg text-accent" style={{ fontFamily: "'Noto Sans Arabic',serif" }}>{w.arabic}</span>
                    <span className="text-xs text-fg-3 flex-1">{w.transliteration}</span>
                    <span className="text-sm">{w.english}</span>
                    <button type="button" onClick={() => playWordClick(w)} className="text-fg-3 hover:text-fg">
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            {mastered && onNextTopic ? (
              <>
                <Button size="lg" onClick={onNextTopic}>
                  Next topic{nextTopicName ? `: ${nextTopicName}` : ""} <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="lg" onClick={onRetry}><RotateCcw className="w-4 h-4" /> Try again</Button>
              </>
            ) : (
              <>
                <Button size="lg" onClick={onRetry}><RotateCcw className="w-4 h-4" /> Try again</Button>
                {onNextTopic && (
                  <Button variant="outline" size="lg" onClick={onNextTopic}>
                    Skip to next <ChevronRight className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="ghost" size="lg" onClick={onHome}>Dashboard <ArrowRight className="w-4 h-4" /></Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </BlurReveal>
  );
}

function NoTopicsCard() {
  return (
    <Card className="text-center">
      <CardContent className="py-12">
        <p className="text-fg-2">No topics found yet.</p>
      </CardContent>
    </Card>
  );
}

function NoVocabCard({ count }: { count: number }) {
  return (
    <Card className="text-center">
      <CardContent className="py-12">
        <p className="text-fg-2">This topic has only {count} words — need at least {MIN_VOCAB}.</p>
        <Button variant="outline" className="mt-4" asChild><Link to="/learn">Browse topics</Link></Button>
      </CardContent>
    </Card>
  );
}

function Spinner() {
  return (
    <div className="py-16 flex items-center justify-center gap-2 text-fg-3">
      <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading topic…
    </div>
  );
}

/* ─────────────── helpers ─────────────── */

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildQuestion(vocab: VocabWord[], excludeIds: (string | number)[]): Question {
  const pool = vocab.filter(w => !excludeIds.includes(w.id));
  const word = (pool.length ? pool : vocab)[Math.floor(Math.random() * (pool.length ? pool.length : vocab.length))];
  const direction: Direction = Math.random() < 0.5 ? "ar-en" : "en-ar";
  const lang: "en" | "ar" = direction === "ar-en" ? "en" : "ar";
  const field = direction === "ar-en" ? "english" : "arabic";

  const distractors = shuffle(vocab.filter(w => w.id !== word.id)).slice(0, 3);
  const all = [word, ...distractors];
  const options: Option[] = shuffle(all.map(w => ({
    id:        String(w.id),
    label:     (w as any)[field] || "—",
    lang,
    isCorrect: w.id === word.id
  })));
  return { word, direction, options };
}
